/**
 * Redux Store Configuration
 *
 * Central Redux store for the Concept Testing application.
 * Currently manages feature flags for dynamic route access control.
 *
 * @author Sabya
 * @date September 30, 2025
 *
 * Features:
 * - Redux Toolkit for simplified Redux setup
 * - Redux DevTools integration (development only)
 * - Automatic middleware configuration
 * - Thunk middleware enabled by default
 *
 * Store Structure:
 * {
 *   features: {
 *     features: Object<string, boolean>,
 *     loading: boolean,
 *     error: string | null,
 *     loadedProjectId: string | null
 *   }
 * }
 *
 * Usage in Components:
 *   import { useSelector, useDispatch } from 'react-redux';
 *   import { fetchFeatureConfig, selectFeatures } from './redux/slices/featureSlice';
 *
 *   const features = useSelector(selectFeatures);
 *   const dispatch = useDispatch();
 *   dispatch(fetchFeatureConfig(projectId));
 *
 * Future Scalability:
 * To add more slices, simply import and add to the reducer object:
 *   reducer: {
 *     features: featureReducer,
 *     user: userReducer,        // Future
 *     projects: projectReducer,  // Future
 *   }
 */

import { configureStore } from '@reduxjs/toolkit';
import featureReducer from './slices/featureSlice';

/**
 * Redux Store Instance
 *
 * Configured with Redux Toolkit's configureStore which automatically:
 * - Sets up Redux DevTools (development only)
 * - Adds thunk middleware for async actions
 * - Enables Redux Toolkit's development checks (immutability, serialization)
 * - Configures middleware chain
 */
export const store = configureStore({
    reducer: {
        features: featureReducer,  // Feature flags slice
        // Add more reducers here as the app grows
    },
    // Redux DevTools enabled only in development
    // In production, this is automatically disabled for performance
    devTools: process.env.NODE_ENV !== 'production',
});

// Export as default for cleaner imports
export default store;